"use client";

import { ArrowLeftIcon, ArrowRightIcon, SealCheckIcon } from "@phosphor-icons/react";
import Image from "next/image";
import { useCallback } from "react";
import QuoteIcon from "@/icons/ui/quotation-mark.svg";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from "~/components/ui/carousel";
import { cn } from "~/lib/utils";
import Lead from "~/componentsDS/Typography/Lead";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Overline from "~/componentsDS/Typography/Overline";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import { useTimedCarousel } from "../../hooks/useTimedCarousel";
import CarouselProgressDots from "../../components/CarouselProgressDots";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { TESTIMONIALS } from "./data";

export default function TestimonialCarousel() {
    const sendEvent = useTrackEvent();
    const handleManualSelect = useCallback((index: number) => {
        const testimonial = TESTIMONIALS[index];
        if (!testimonial) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "quotes",
                meta_data: {
                    image_person_name: testimonial.author,
                    product_name: "libida",
                },
            },
        });
    }, [sendEvent]);
    const {
        setApi,
        opts,
        plugins,
        selectedIndex,
        enterIndex,
        carouselRef,
        progressFillRef,
        scrollPrev,
        scrollNext,
        scrollTo,
    } = useTimedCarousel({
        onManualSelect: handleManualSelect,
    });

    return (
        <article
            ref={carouselRef}
            className="group/testimonials relative min-h-[404px] overflow-hidden rounded-2xl bg-trueBlack/25 text-pureWhite backdrop-blur-[25px] lg:h-[1010px]"
        >
            <style href="testimonial-carousel-enter" precedence="default">{`
                @keyframes testimonial-slide-in-right {
                    from {
                        opacity: 0;
                        transform: translateX(48px);
                    }

                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @media (prefers-reduced-motion: no-preference) {
                    .testimonial-enter {
                        animation: testimonial-slide-in-right 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
                    }
                }
            `}</style>

            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[90%] aspect-[1704/1338] hidden overflow-hidden lg:block">
                <Image
                    src="https://res.cloudinary.com/dirscuuhp/image/upload/v1789482751/new_design/Frame_2147205497_1_asdwiz.webp"
                    alt="A hand holding a Libida tablet"
                    fill
                    quality={95}
                    sizes="(min-width: 1024px) 800px, 0px"
                    loading="lazy"
                    className="object-cover object-bottom"
                />
            </div>

            <Carousel
                setApi={setApi}
                loop
                desktopWheelGestures
                opts={opts}
                plugins={plugins}
                className="relative z-[1] h-full"
            >
                <CarouselContent className="ml-0 h-full">
                    {TESTIMONIALS.map((testimonial, index) => (
                        <CarouselItem key={testimonial.author} className="h-full pl-0 flex h-auto">
                            <div
                                className={cn(
                                    "flex flex-col px-8 pt-6 pb-8 min-[399px]:pb-6 lg:px-16 lg:pb-0 lg:pt-12 lg:h-[1010px]",
                                    index !== selectedIndex && "opacity-0 group-data-[dragging=true]/testimonials:opacity-100",
                                    index === enterIndex && "testimonial-enter"
                                )}
                            >
                                <QuoteIcon className="mb-6 lg:mb-8 size-8 shrink-0" />

                                <Lead
                                    as="i"
                                    size="lg"
                                    font="featureDisplay"
                                    className="max-lg:text-[24px]"
                                >
                                    {testimonial.title}
                                </Lead>

                                <Paragraph
                                    size="lg"
                                    className="mt-6 lg:mt-8 lg:font-featureDisplay lg:text-[24px] lg:tracking-[0.48px] lg:leading-[1.2]"
                                >
                                    {testimonial.copy}
                                </Paragraph>

                                <div className="mt-auto lg:mt-12 pt-6 lg:pt-0 flex items-center gap-3">
                                    <Image
                                        src={testimonial.avatarUrl}
                                        alt=""
                                        width={40}
                                        height={40}
                                        sizes="40px"
                                        loading="lazy"
                                        className="size-10 rounded-full object-cover"
                                    />
                                    <div>
                                        <Overline size="lg">
                                            {testimonial.author}, {testimonial.age}
                                        </Overline>
                                        <Overline size="xs" className="flex items-center gap-1 mt-0.5">
                                            <SealCheckIcon weight="fill" className="size-[18px] text-pureWhite lg:text-primary300" />
                                            <TextWithRegisteredMark text="Verified Libida® patient" className="inline-block pt-[2px]" />
                                        </Overline>
                                    </div>
                                </div>
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselContent>

                <button
                    type="button"
                    onClick={scrollPrev}
                    aria-label="Previous testimonial"
                    className="absolute left-4 top-[184px] z-[2] hidden size-8 items-center justify-center rounded-full bg-[#160e1a]/90 text-pureWhite opacity-0 transition-[opacity,background-color] duration-300 group-hover/testimonials:opacity-100 hover:bg-primary300 lg:flex"
                >
                    <ArrowLeftIcon className="size-5" />
                </button>
                <button
                    type="button"
                    onClick={scrollNext}
                    aria-label="Next testimonial"
                    className="absolute right-4 top-[184px] z-[2] hidden size-8 items-center justify-center rounded-full bg-[#160e1a]/90 text-pureWhite opacity-0 transition-[opacity,background-color] duration-300 group-hover/testimonials:opacity-100 hover:bg-primary300 lg:flex"
                >
                    <ArrowRightIcon className="size-5" />
                </button>

                <CarouselProgressDots
                    count={TESTIMONIALS.length}
                    selectedIndex={selectedIndex}
                    progressFillRef={progressFillRef}
                    onSelect={scrollTo}
                    label="testimonial"
                    className="absolute bottom-3 right-3 z-[2] bg-trueBlack/25 lg:bottom-5 lg:right-5 lg:bg-trueBlack/75"
                    dotClassName="bg-gray100 lg:bg-gray200"
                />
            </Carousel>
        </article>
    );
}
